'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { supabase } from '@/config/clients/supabase'
import { stashPostLoginRedirect, takePostLoginRedirect } from '@/lib/post-login-redirect'
import { useUserStore, type ForcedLogoutReason } from './useUser'
import { forcedLogoutLoginUrl } from '@/lib/auth/forced-logout'
import { fetchUserFromServerAction } from '@/app/actions/user.actions'

export function useSignIn() {
	const [isLoading, setIsLoading] = useState(false)

	const signIn = async (returnTo?: string) => {
		try {
			setIsLoading(true)

			// Guarda o destino pretendido para o pós-login (sobrevive ao round-trip
			// do OAuth via sessionStorage; lido no /auth/callback).
			stashPostLoginRedirect(returnTo)

			const { error } = await supabase.auth.signInWithOAuth({
				provider: 'google',
				options: {
					redirectTo: `${globalThis.location.origin}/auth/callback`,
				},
			})

			if (error) {
				console.error('Error signing in:', error.message)
				throw error
			}
		} catch (error) {
			console.error('Sign in error:', error)
			throw error
		} finally {
			setIsLoading(false)
		}
	}

	return { signIn, isLoading }
}

export function useSignOut() {
	const [isLoading, setIsLoading] = useState(false)
	const { clearUser, clearForcedLogoutNotice } = useUserStore()
	const router = useRouter()

	const signOut = async () => {
		const finalizeLogout = () => {
			clearUser()
			clearForcedLogoutNotice()
			router.push('/login')
		}

		try {
			setIsLoading(true)

			const {
				data: { session },
			} = await supabase.auth.getSession()

			if (!session) return finalizeLogout()

			const { error } = await supabase.auth.signOut()
			if (error) console.error('Error signing out:', error.message)
			return finalizeLogout()
		} catch (error) {
			console.error('Sign out error:', error)
			return finalizeLogout()
		} finally {
			setIsLoading(false)
		}
	}

	return { signOut, isLoading }
}

export function useAuthSession() {
	const [isInitialized, setIsInitialized] = useState(false)
	const { supabaseUser, setUser, setDatabaseUser, clearUser, refreshDatabaseUser, setForcedLogoutNotice } =
		useUserStore()
	const router = useRouter()
	const isForcingLogout = useRef(false)
	const isCheckingStatus = useRef(false)

	const handleForcedLogout = useCallback(
		async (reason: ForcedLogoutReason) => {
			if (isForcingLogout.current) return
			isForcingLogout.current = true
			try {
				setForcedLogoutNotice(reason)
				const {
					data: { session },
				} = await supabase.auth.getSession()
				if (session) {
					const { error } = await supabase.auth.signOut()
					if (error) console.error('Forced sign out error:', error.message)
				}
			} catch (error) {
				console.error('Forced sign out error:', error)
			} finally {
				clearUser()
				// replace (não push) com ?reason= — mesma linguagem do middleware; a
				// página de login mostra o motivo em vez de um logout mudo.
				if (typeof window === 'undefined' || globalThis.location.pathname !== '/login') {
					router.replace(forcedLogoutLoginUrl(reason))
				}
				isForcingLogout.current = false
			}
		},
		[clearUser, router, setForcedLogoutNotice],
	)

	useEffect(() => {
		// Sincroniza o usuário autenticado com o store (mesma lógica usada na
		// inicialização e no listener de auth state) e força logout se o usuário
		// sumiu (missing) ou está inativo. Retorna false quando deve curto-circuitar
		// (logout forçado), preservando o early-return dos call-sites originais.
		const syncSessionUser = async (sessionUser: NonNullable<Parameters<typeof setUser>[0]>) => {
			setUser(sessionUser)

			const databaseUserData = await fetchUserFromServerAction(sessionUser.id)
			if (!databaseUserData) {
				if (typeof navigator === 'undefined' || navigator.onLine) {
					await handleForcedLogout('missing')
				}
				return false
			}
			if (!databaseUserData.is_active) {
				await handleForcedLogout('inactive')
				return false
			}
			setDatabaseUser(databaseUserData)
			return true
		}

		const handleAuthStateChange = (event: string) => {
			if (event === 'SIGNED_IN') {
				if (globalThis.location.pathname === '/login') {
					router.push(takePostLoginRedirect())
				}
			} else if (event === 'SIGNED_OUT') {
				if (globalThis.location.pathname !== '/login' && globalThis.location.pathname !== '/auth/callback') {
					router.push('/login')
				}
			}
		}

		const initializeAuth = async () => {
			try {
				const {
					data: { session },
					error,
				} = await supabase.auth.getSession()

				if (error) {
					console.error('Error getting session:', error.message)
					return
				}

				if (session?.user) {
					// Destrava a UI (AuthGuard) assim que a sessão local é conhecida — o
					// sync com o banco envolve rede e NÃO pode segurar o isInitialized:
					// se a API pendurar, o app inteiro ficaria em "Verificando
					// autenticação..." para sempre.
					setUser(session.user)
					setIsInitialized(true)
					await syncSessionUser(session.user)
				} else {
					clearUser()
				}
			} catch (error) {
				console.error('Initialize error:', error)
			} finally {
				setIsInitialized(true)
			}
		}

		initializeAuth()

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			if (session?.user) {
				const shouldContinue = await syncSessionUser(session.user)
				if (!shouldContinue) return
			} else {
				clearUser()
			}

			handleAuthStateChange(event)
		})

		return () => {
			subscription.unsubscribe()
		}
	}, [setUser, setDatabaseUser, clearUser, router, handleForcedLogout])

	useEffect(() => {
		const checkUserStatus = async () => {
			if (isCheckingStatus.current) return
			if (!supabaseUser) return
			isCheckingStatus.current = true
			try {
				const updatedUser = await refreshDatabaseUser()
				if (!supabaseUser) return
				if (!updatedUser) {
					if (typeof navigator === 'undefined' || navigator.onLine) {
						await handleForcedLogout('missing')
					}
					return
				}
				if (!updatedUser.is_active) {
					await handleForcedLogout('inactive')
				}
			} catch (error) {
				console.error('Visibility refresh error:', error)
			} finally {
				isCheckingStatus.current = false
			}
		}

		const handleVisibilityChange = () => {
			if (document.hidden) return
			checkUserStatus()
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [supabaseUser, refreshDatabaseUser, handleForcedLogout])

	return { user: supabaseUser, isInitialized }
}

export function useRefreshUser() {
	const { refreshDatabaseUser } = useUserStore()
	const [isRefreshing, setIsRefreshing] = useState(false)

	const refresh = async () => {
		try {
			setIsRefreshing(true)
			await refreshDatabaseUser()
		} catch (error) {
			console.error('Error refreshing user data:', error)
		} finally {
			setIsRefreshing(false)
		}
	}

	return { refresh, isRefreshing }
}
